import React, { useState, useCallback } from 'react';
import { useParams } from 'react-router';
import Button, { LoadingButton } from '@atlaskit/button';
import defaultTo from 'lodash/defaultTo';
import Avatar from '@atlaskit/avatar';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { Box, Flex, Text, xcss } from '@atlaskit/primitives';
import { patchContactRelationship, deleteContactRelationship } from '@atlassian/temp-proxies';
import {
  errorNotificationBuilder,
  MoreActionsItem,
  NotificationTypes,
  successNotificationBuilder,
  useNotification,
  MoreActions,
} from '@css-xp/base';
import { ContactInfo } from '@css-xp/graphtypes/src/generated-types-new';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, AdvocateAccessWall } from '@css-xp/ppc-access-control';
import { getPartnerServiceDeskUrlForGenericHelp } from '@css-xp/ppc-navigation';
import { failureMessage } from '@css-xp/ppc-common';
import { useContactServicePayload } from '@css-xp/contact-resources';
import { useOpenClose } from '@css-xp/ppc-custom-hooks';

interface UserDetails {
  accountId: string;
  picture: string;
  name: string;
  email?: string;
  isPrimary?: boolean;
}

const styledCustomerContact = xcss({
  paddingTop: 'space.025',
  paddingBottom: 'space.150',
});

interface CustomerContactActionsProps {
  invoiceGroupId: string;
  user: UserDetails;
  customerName: string;
  isPrimaryCustomerContactPresent: boolean;
  refetch: () => void;
  shouldDisableAllActions?: boolean;
  shouldRenderForInternalUse?: boolean;
  isCustomerContactsEditable?: boolean;
  primaryContactDetails?: ContactInfo;
  validPartnerDomains?: string[];
}

export const CustomerContactActions = ({
  user,
  invoiceGroupId,
  customerName,
  isPrimaryCustomerContactPresent,
  refetch,
  shouldDisableAllActions,
  shouldRenderForInternalUse,
  isCustomerContactsEditable,
  primaryContactDetails,
  validPartnerDomains,
}: CustomerContactActionsProps) => {
  const primaryCustomerText = shouldRenderForInternalUse ? 'key' : 'primary customer';
  const [isSetPrimaryContactLoading, setIsSetPrimaryContactLoading] = useState(false);
  const [isRemoveContactLoading, setIsRemoveContactLoading] = useState(false);
  const { notificationSent, sendSuccessNotification, setNotificationSent, sendErrorNotification } = useNotification();
  const { txaId } = useParams<{ txaId: string }>();
  const {
    getPatchPayloadForSetPrimaryContact,
    getDeleteContactRelationshipsPayload,
    getPatchPayloadForAddingAndRemovingPrimaryContact,
  } = useContactServicePayload();
  const {
    isOpen: isSetPrimaryContactModalOpen,
    open: openSetPrimaryContactModal,
    close: closeSetPrimaryContactModal,
  } = useOpenClose();

  const {
    isOpen: isRemoveContactModalOpen,
    open: openRemoveContactModal,
    close: closeRemoveContactModal,
  } = useOpenClose();
  const {
    isOpen: isReplacePrimaryContactModalOpen,
    open: openReplacePrimaryContactModal,
    close: closeReplacePrimaryContactModal,
  } = useOpenClose();

  const isPrimaryOrKeyContact = !!user?.isPrimary;
  const {
    isOpen: isPrimaryContactInvalidDomain,
    open: openPrimaryContactInvalidDomainModal,
    close: closePrimaryContactInvalidDomainModal,
  } = useOpenClose();

  const isUserFromPartnerDomain = () => {
    const domainFromPrimaryCustomerContact = user?.email?.split('@')?.[1];
    if (
      domainFromPrimaryCustomerContact &&
      defaultTo(validPartnerDomains, []).includes(domainFromPrimaryCustomerContact.toLowerCase())
    ) {
      return true;
    }
    return false;
  };

  const getContactAvatarAndName = () => {
    return (
      <Flex gap="space.100" xcss={styledCustomerContact} alignItems="center">
        <Avatar src={user.picture} size="large" />
        <Flex direction="column">
          <Text>{user.name || ''}</Text>
          <Text size="small" color="color.text.subtle">
            {user.email || ''}
          </Text>
        </Flex>
      </Flex>
    );
  };

  const handleSetPrimaryContactConfirmClick = useCallback(async () => {
    try {
      setIsSetPrimaryContactLoading(true);
      const patchContactRelationshipPayload = isPrimaryCustomerContactPresent
        ? getPatchPayloadForAddingAndRemovingPrimaryContact({
            invoiceGroupId,
            transactionAccountId: txaId,
            newPrimaryContactId: user.accountId,
            existingPrimaryContactId: primaryContactDetails?.accountId || '',
          })
        : getPatchPayloadForSetPrimaryContact({
            invoiceGroupId,
            primaryContactId: user.accountId,
            transactionAccountId: txaId,
          });
      await patchContactRelationship(patchContactRelationshipPayload);
      if (!notificationSent) {
        const notificationOptions = successNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader(
            shouldRenderForInternalUse
              ? `Key contact ${isPrimaryCustomerContactPresent ? `updated` : `saved`}`
              : `Primary customer contact ${isPrimaryCustomerContactPresent ? `updated` : `saved`}`
          )
          .withDismissAfter(5000)
          .build();
        sendSuccessNotification(notificationOptions);
        setNotificationSent(true);
      }
      setIsSetPrimaryContactLoading(false);
      closeSetPrimaryContactModal();
      refetch();
    } catch (e) {
      if (!notificationSent) {
        const notificationOptions = errorNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader(
            `Unable to ${isPrimaryCustomerContactPresent ? 'update ' : 'save '} ${primaryCustomerText} contact`
          )
          .withMessage(failureMessage())
          .withDismissAfter(5000)
          .build();
        sendErrorNotification(notificationOptions);
        setNotificationSent(true);
      }
      setIsSetPrimaryContactLoading(false);
      closeSetPrimaryContactModal();
    }
  }, [
    closeSetPrimaryContactModal,
    getPatchPayloadForSetPrimaryContact,
    invoiceGroupId,
    notificationSent,
    refetch,
    sendErrorNotification,
    sendSuccessNotification,
    setNotificationSent,
    txaId,
    user.accountId,
  ]);

  const handleRemoveCustomerContacts = useCallback(async () => {
    try {
      setIsRemoveContactLoading(true);
      const deleteContactRelationshipPayload = getDeleteContactRelationshipsPayload({
        invoiceGroupId: invoiceGroupId,
        transactionAccountId: txaId,
        deleteContactIds: [user.accountId],
      });
      await deleteContactRelationship(deleteContactRelationshipPayload as any);
      if (!notificationSent) {
        const notificationOptions = successNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader(shouldRenderForInternalUse ? 'Contact removed' : 'Customer contact removed')
          .withDismissAfter(5000)
          .build();
        sendSuccessNotification(notificationOptions);
        setNotificationSent(true);
      }
      setIsRemoveContactLoading(false);
      closeRemoveContactModal();
      refetch();
    } catch (e) {
      if (!notificationSent) {
        const notificationOptions = errorNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader(
            shouldRenderForInternalUse
              ? `Unable to remove contact, try again.`
              : `Unable to remove customer contact, try again.`
          )
          .withMessage(failureMessage())
          .withDismissAfter(5000)
          .build();
        sendErrorNotification(notificationOptions);
        setNotificationSent(true);
      }
      setIsRemoveContactLoading(false);
      closeRemoveContactModal();
    }
  }, [
    closeRemoveContactModal,
    getDeleteContactRelationshipsPayload,
    invoiceGroupId,
    notificationSent,
    refetch,
    sendErrorNotification,
    sendSuccessNotification,
    setNotificationSent,
    txaId,
    user.accountId,
  ]);

  const moreActions: MoreActionsItem[] = [
    {
      id: 'set_as_primary',
      content: `Set as ${shouldRenderForInternalUse ? `key contact` : `primary`}`,
      action: () => {
        if (!shouldRenderForInternalUse && isUserFromPartnerDomain()) {
          openPrimaryContactInvalidDomainModal();
        } else openSetPrimaryContactModal();
      },
      predicate: !user?.isPrimary,
    },
    {
      id: 'remove',
      content: 'Remove contact',
      action: () => openRemoveContactModal(),
    },
  ];

  return (
    <>
      <ModalTransition>
        {isSetPrimaryContactModalOpen && (
          <Modal onClose={closeSetPrimaryContactModal} testId="remove-billing-admin-modal">
            <ModalHeader>
              <ModalTitle>Confirm {primaryCustomerText} contact</ModalTitle>
            </ModalHeader>
            <ModalBody>
              {isPrimaryCustomerContactPresent ? (
                <Flex gap="space.100" direction="column">
                  <Text>New {primaryCustomerText} contact for this billing profile will be </Text>
                  {getContactAvatarAndName()}
                </Flex>
              ) : (
                <>
                  <Text weight="bold">
                    {user.name} ({user.email})
                  </Text>{' '}
                  <Text>
                    will become the {primaryCustomerText} contact
                    {customerName ? ` for ${customerName}` : ''}, and receive all emails and subscription keys linked to
                    this billing profile.
                  </Text>
                  <br />
                  <br />
                  <Text>You can always change your {primaryCustomerText} contact by contacting us.</Text>
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <Button appearance="subtle" onClick={closeSetPrimaryContactModal}>
                Cancel
              </Button>
              <LoadingButton
                isLoading={isSetPrimaryContactLoading}
                appearance="primary"
                onClick={handleSetPrimaryContactConfirmClick}
                autoFocus
                testId="set-primary-confirm-button"
              >
                Confirm
              </LoadingButton>
            </ModalFooter>
          </Modal>
        )}
        {isReplacePrimaryContactModalOpen && (
          <Modal onClose={closeReplacePrimaryContactModal} testId="remove-billing-admin-modal">
            <ModalHeader>
              <ModalTitle>Contact us</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <Text>Raise a request to change your {primaryCustomerText} contact for this billing profile.</Text>
            </ModalBody>
            <ModalFooter>
              <Button appearance="subtle" onClick={closeReplacePrimaryContactModal}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                autoFocus
                target="_blank"
                href={getPartnerServiceDeskUrlForGenericHelp()}
                testId="contact-support-button"
              >
                Contact support
              </Button>
            </ModalFooter>
          </Modal>
        )}
        {isRemoveContactModalOpen && (
          <Modal onClose={closeRemoveContactModal} testId="remove-billing-contact-modal" width={500}>
            <ModalHeader>
              {isPrimaryOrKeyContact ? (
                <ModalTitle>Unable to remove {shouldRenderForInternalUse ? `key` : `primary`} contact</ModalTitle>
              ) : (
                <ModalTitle>Remove {shouldRenderForInternalUse ? `key` : `customer`} contact</ModalTitle>
              )}
            </ModalHeader>
            <ModalBody>
              {isPrimaryOrKeyContact ? (
                <Text>
                  {`You can remove this contact only after selecting another ${
                    shouldRenderForInternalUse ? `key ` : `primary `
                  } contact for this billing profile.`}
                </Text>
              ) : (
                <>
                  <Text>You're about to remove a contact from your billing profile.</Text>
                  <br />
                  <br />
                  {getContactAvatarAndName()}
                  <Text>
                    Once removed, they'll lose access to partner-managed subscriptions and subscription notifications.
                  </Text>
                  <br />
                  <Box paddingBlockStart="space.200">
                    <Text>We'll notify the customer about the removal. You can always add them later.</Text>
                  </Box>
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <Button appearance="subtle" onClick={closeRemoveContactModal}>
                {isPrimaryOrKeyContact ? `Close` : `Cancel`}
              </Button>
              {!isPrimaryOrKeyContact && (
                <LoadingButton
                  isLoading={isRemoveContactLoading}
                  appearance="primary"
                  target="_blank"
                  onClick={handleRemoveCustomerContacts}
                  testId="remove-contact"
                >
                  Remove
                </LoadingButton>
              )}
            </ModalFooter>
          </Modal>
        )}
        {isPrimaryContactInvalidDomain && (
          <Modal onClose={closePrimaryContactInvalidDomainModal} testId="remove-billing-admin-modal">
            <ModalHeader>
              <ModalTitle>Primary contact can't be changed</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <Text>
                {user?.name} ({user?.email}) can't be added as a primary contact since they belong to the partner
                company. Choose a contact associated with the customer domain as the primary contact.
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button appearance="subtle" onClick={closePrimaryContactInvalidDomainModal}>
                Okay
              </Button>
            </ModalFooter>
          </Modal>
        )}
      </ModalTransition>
      <AdvocateAccessWall
        accessType={ADVOCATE_ACCESS_TYPE.UPDATE}
        resource={ADVOCATE_RESOURCE_TYPE.END_CUSTOMER_CONTACT}
      >
        {!isCustomerContactsEditable && isPrimaryCustomerContactPresent && user.isPrimary && (
          <Box paddingInlineEnd="space.100">
            <Button
              onClick={openReplacePrimaryContactModal}
              testId="replace-primary-button"
              appearance="subtle"
              isDisabled={shouldDisableAllActions}
            >
              {shouldRenderForInternalUse ? 'Change key contact' : 'Change primary contact'}
            </Button>
          </Box>
        )}
      </AdvocateAccessWall>
      <AdvocateAccessWall
        accessType={ADVOCATE_ACCESS_TYPE.UPDATE}
        resource={ADVOCATE_RESOURCE_TYPE.END_CUSTOMER_CONTACT}
      >
        {!isPrimaryCustomerContactPresent && (
          <Box paddingInlineEnd="space.100">
            <Button
              onClick={openSetPrimaryContactModal}
              testId="set-primary-button"
              appearance="subtle"
              isDisabled={shouldDisableAllActions}
            >
              {shouldRenderForInternalUse ? 'Set as key contact' : 'Set as primary'}
            </Button>
          </Box>
        )}
      </AdvocateAccessWall>
      {isCustomerContactsEditable && (
        <AdvocateAccessWall
          accessType={ADVOCATE_ACCESS_TYPE.UPDATE}
          resource={ADVOCATE_RESOURCE_TYPE.END_CUSTOMER_CONTACT}
        >
          <MoreActions actions={moreActions} />
        </AdvocateAccessWall>
      )}
    </>
  );
};
